import React, { useRef, useCallback, useMemo, createContext, useContext } from 'react';

type OpenFn = () => void;
type CommitFn = () => Promise<boolean>;

export type InlineEditingContextValue = {
  registerCell(id: string, openFn: OpenFn, commitFn: CommitFn): void;
  unregisterCell(id: string): void;
  requestOpen(id: string): void;
  onSaveStart(id: string): void;
  onSaveResolve(id: string, success: boolean): void;
  setActiveCellId(id: string): void;
  clearActiveCellId(id: string): void;
  getActiveCellId(): string | null;
  getSavingCellId(): string | null;
  commitCell(id: string): Promise<boolean>;
};

const InlineEditingContext = createContext<InlineEditingContextValue | null>(null);

export const InlineEditingContextProvider = ({ children }: { children: React.ReactNode }) => {
  const savingCellIdRef = useRef<string | null>(null);
  const openFnsRef = useRef<Map<string, OpenFn>>(new Map());
  const commitFnsRef = useRef<Map<string, CommitFn>>(new Map());
  const activeCellIdRef = useRef<string | null>(null);

  const registerCell = useCallback((id: string, openFn: OpenFn, commitFn: CommitFn) => {
    openFnsRef.current.set(id, openFn);
    commitFnsRef.current.set(id, commitFn);
  }, []);

  const unregisterCell = useCallback((id: string) => {
    openFnsRef.current.delete(id);
    commitFnsRef.current.delete(id);
  }, []);

  const requestOpen = useCallback((id: string) => {
    const activeId = activeCellIdRef.current;
    if (activeId === id) return; // already open
    if (activeId && activeId !== id) return; // another cell is active; let it close first
    if (savingCellIdRef.current !== null) return; // another cell is saving; ignore
    const openFn = openFnsRef.current.get(id);
    openFn?.();
  }, []);

  const onSaveStart = useCallback((id: string) => {
    savingCellIdRef.current = id;
  }, []);

  const onSaveResolve = useCallback((id: string, _success: boolean) => {
    savingCellIdRef.current = null;
    // No pending-open queue — user must explicitly click the target cell again.
  }, []);

  const setActiveCellId = useCallback((id: string) => {
    activeCellIdRef.current = id;
    document.body.setAttribute('data-inline-editing-active', 'true');
  }, []);

  const clearActiveCellId = useCallback((id: string) => {
    if (activeCellIdRef.current === id) {
      activeCellIdRef.current = null;
      document.body.removeAttribute('data-inline-editing-active');
    }
  }, []);

  const getActiveCellId = useCallback(() => activeCellIdRef.current, []);

  const getSavingCellId = useCallback(() => savingCellIdRef.current, []);

  const commitCell = useCallback(async (id: string) => {
    return commitFnsRef.current.get(id)?.() ?? false;
  }, []);

  const value = useMemo<InlineEditingContextValue>(
    () => ({
      registerCell,
      unregisterCell,
      requestOpen,
      onSaveStart,
      onSaveResolve,
      setActiveCellId,
      clearActiveCellId,
      getActiveCellId,
      getSavingCellId,
      commitCell,
    }),
    [registerCell, unregisterCell, requestOpen, onSaveStart, onSaveResolve, setActiveCellId, clearActiveCellId, getActiveCellId, getSavingCellId, commitCell],
  );

  return <InlineEditingContext.Provider value={value}>{children}</InlineEditingContext.Provider>;
};

export const useInlineEditing = () => {
  const ctx = useContext(InlineEditingContext);
  if (!ctx) throw new Error('useInlineEditing must be used inside InlineEditingContextProvider');
  return ctx;
};
